import { InsightBanner } from '@siliconx/shared/ui/InsightBanner';
import type { BenchRunBmcOverrideView, BenchRunDetail } from '@siliconx/shared';
import { formatBenchWhen } from './benchTime';

/**
 * SIL-2113. The bench tool can set a ONE-TIME boot override on the machine's BMC (remote management controller) so the machine
 * boots the next tool image from the network. If a run ends before the tool confirms it cleared that override, the unit may
 * boot once from the network at its next start, and virtual media may still be mounted. This banner says so and gives the manual
 * cleanup step. It is advisory: it never blocks anything, and it is shown only to members of the run's own organisation (the
 * API reads it under the portal's row-level scope).
 *
 * The text is static. The only interpolations are a formatted date and the boot number, both rendered as plain text by React;
 * nothing from the agent (no code, address or account) reaches this component, so there is nothing to escape beyond that.
 * Do NOT add a "power the machine on once to use it up" hint: that is the very network boot the residue causes.
 */

export const BMC_OVERRIDE_COPY: Record<BenchRunBmcOverrideView['state'], { lead: string; body: string }> = {
  may_remain: {
    lead: 'A one-time boot override may still be set on this machine',
    body:
      "This run ended before the tool confirmed the machine's one-time boot override was cleared. To be safe, open the machine's BMC "
      + '(remote management) boot settings, set the one-time boot override to Disabled and eject any virtual media before the next '
      + 'power-on. If the machine boots from the network unexpectedly, power it off and clear the setting.',
  },
  reset_failed: {
    lead: 'The tool could not confirm it cleared the one-time boot override',
    body:
      'Before the next reboot, set the one-time boot override to Disabled in the machine\'s BMC (remote management) boot settings '
      + 'and eject any virtual media.',
  },
};

export function BmcOverrideBanner({ run }: { run: BenchRunDetail }) {
  const o = run.bmcOverride;
  if (!o) return null;
  // Closed map: an unknown state from a newer API renders nothing rather than guessed text.
  const copy = Object.prototype.hasOwnProperty.call(BMC_OVERRIDE_COPY, o.state) ? BMC_OVERRIDE_COPY[o.state] : null;
  if (!copy) return null;
  return (
    <div data-testid="bmc-override-banner" data-state={o.state}>
      <InsightBanner tone="amber" lead={copy.lead}>
        <p>{copy.body}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Noted {formatBenchWhen(o.observedAt)} during boot {Number(o.segmentIdx) + 1} of this run.
        </p>
      </InsightBanner>
    </div>
  );
}
